import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RelatedClaimsPanel = ({ relatedClaims, narrativeConnections, onClaimClick, onNarrativeClick }) => {
  const [activeTab, setActiveTab] = useState('related');

  const getVerdictColor = (verdict) => {
    switch (verdict?.toLowerCase()) {
      case 'true': return 'text-success bg-success/10';
      case 'false': return 'text-destructive bg-destructive/10';
      case 'misleading': return 'text-warning bg-warning/10';
      case 'unverified': return 'text-muted-foreground bg-muted';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getSimilarityColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-muted-foreground';
  };

  const renderRelatedClaims = () => (
    <div className="space-y-4">
      {relatedClaims?.map((claim, index) => (
        <div 
          key={index} 
          className="border border-border rounded-lg p-4 hover:shadow-soft transition-smooth cursor-pointer"
          onClick={() => onClaimClick(claim?.id)}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 pr-4">
              <h4 className="font-medium text-foreground line-clamp-2 mb-2">{claim?.statement}</h4>
              <div className="flex items-center space-x-3 text-sm text-muted-foreground">
                <span>{claim?.date}</span>
                <span>•</span>
                <span>{claim?.country}</span>
                <span>•</span>
                <span>{claim?.language}</span>
              </div>
            </div>
            <div className="flex flex-col items-end space-y-2">
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${getVerdictColor(claim?.verdict)}`}>
                {claim?.verdict}
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Target" size={14} className={getSimilarityColor(claim?.similarity)} />
                <span className={`text-xs font-medium ${getSimilarityColor(claim?.similarity)}`}>
                  {claim?.similarity}% similar
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 text-xs text-muted-foreground">
              <div className="flex items-center space-x-1">
                <Icon name="Eye" size={12} />
                <span>{claim?.views?.toLocaleString()}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="MessageCircle" size={12} />
                <span>{claim?.engagements?.toLocaleString()}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Share" size={12} />
                <span>{claim?.shares?.toLocaleString()}</span>
              </div>
            </div>
            <Button variant="ghost" size="sm" iconName="ExternalLink">
              View Analysis
            </Button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderNarrativeConnections = () => (
    <div className="space-y-4">
      {narrativeConnections?.map((narrative, index) => (
        <div 
          key={index} 
          className="border border-border rounded-lg p-4 hover:shadow-soft transition-smooth cursor-pointer"
          onClick={() => onNarrativeClick(narrative?.id)}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 pr-4">
              <h4 className="font-medium text-foreground mb-2">{narrative?.title}</h4>
              <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{narrative?.description}</p>
              <div className="flex items-center space-x-3 text-sm text-muted-foreground">
                <span>{narrative?.timeframe}</span>
                <span>•</span>
                <span>{narrative?.countries?.join(', ')}</span>
              </div>
            </div>
            <div className="flex flex-col items-end space-y-2">
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                narrative?.status === 'active' ? 'text-warning bg-warning/10' :
                narrative?.status === 'resolved'? 'text-success bg-success/10' : 'text-muted-foreground bg-muted'
              }`}>
                {narrative?.status}
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="TrendingUp" size={14} className="text-primary" />
                <span className="text-xs font-medium text-primary">
                  {narrative?.claimCount} claims
                </span>
              </div>
            </div>
          </div>
          
          {/* Narrative Pattern Visualization */}
          <div className="mb-3">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="GitBranch" size={14} className="text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">Pattern Evolution</span>
            </div>
            <div className="flex items-center space-x-1">
              {narrative?.evolution?.map((stage, stageIndex) => (
                <div key={stageIndex} className="flex items-center">
                  <div className={`w-3 h-3 rounded-full ${
                    stage?.intensity === 'high' ? 'bg-destructive' :
                    stage?.intensity === 'medium'? 'bg-warning' : 'bg-success'
                  }`} />
                  {stageIndex < narrative?.evolution?.length - 1 && (
                    <div className="w-4 h-0.5 bg-border mx-1" />
                  )}
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 text-xs text-muted-foreground">
              <div className="flex items-center space-x-1">
                <Icon name="Globe" size={12} />
                <span>{narrative?.reach?.toLocaleString()} reach</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="AlertTriangle" size={12} />
                <span>Risk: {narrative?.riskLevel}</span>
              </div>
            </div>
            <Button variant="ghost" size="sm" iconName="GitBranch">
              Explore Narrative
            </Button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderCrossCountryPatterns = () => (
    <div className="space-y-4">
      <div className="bg-muted/30 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-3">
          <Icon name="Globe" size={18} className="text-primary" />
          <h4 className="font-medium text-foreground">Cross-Border Coordination</h4>
        </div>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between p-2 bg-card rounded border border-border">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-destructive rounded-full" />
              <span className="text-sm text-foreground">Germany → France</span>
            </div>
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              <Icon name="Clock" size={12} />
              <span>2 hours delay</span>
            </div>
          </div>
          
          <div className="flex items-center justify-between p-2 bg-card rounded border border-border">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-warning rounded-full" />
              <span className="text-sm text-foreground">France → Spain</span>
            </div>
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              <Icon name="Clock" size={12} />
              <span>6 hours delay</span>
            </div>
          </div>
          
          <div className="flex items-center justify-between p-2 bg-card rounded border border-border">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-success rounded-full" />
              <span className="text-sm text-foreground">Spain → Italy</span>
            </div>
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              <Icon name="Clock" size={12} />
              <span>12 hours delay</span>
            </div>
          </div>
        </div>
        
        <div className="mt-3 pt-3 border-t border-border">
          <Button variant="outline" size="sm" iconName="Map" fullWidth>
            View Coordination Map
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-lg shadow-soft">
      {/* Tab Navigation */}
      <div className="border-b border-border">
        <nav className="flex space-x-1 p-1">
          <button
            onClick={() => setActiveTab('related')}
            className={`
              flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-smooth
              ${activeTab === 'related' ?'bg-primary text-primary-foreground shadow-soft' :'text-muted-foreground hover:text-foreground hover:bg-muted'
              }
            `}
          >
            <Icon name="Link" size={16} />
            <span>Related Claims</span>
            <span className={`
              px-1.5 py-0.5 rounded-full text-xs font-medium
              ${activeTab === 'related' ?'bg-primary-foreground/20 text-primary-foreground' :'bg-muted text-muted-foreground'
              }
            `}>
              {relatedClaims?.length}
            </span>
          </button>
          
          <button
            onClick={() => setActiveTab('narratives')}
            className={`
              flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-smooth
              ${activeTab === 'narratives' ?'bg-primary text-primary-foreground shadow-soft' :'text-muted-foreground hover:text-foreground hover:bg-muted'
              }
            `}
          >
            <Icon name="GitBranch" size={16} />
            <span>Narratives</span>
            <span className={`
              px-1.5 py-0.5 rounded-full text-xs font-medium
              ${activeTab === 'narratives' ?'bg-primary-foreground/20 text-primary-foreground' :'bg-muted text-muted-foreground'
              }
            `}>
              {narrativeConnections?.length}
            </span>
          </button>
          
          <button
            onClick={() => setActiveTab('patterns')}
            className={`
              flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-smooth
              ${activeTab === 'patterns' ?'bg-primary text-primary-foreground shadow-soft' :'text-muted-foreground hover:text-foreground hover:bg-muted'
              }
            `}
          >
            <Icon name="Globe" size={16} />
            <span>Patterns</span>
          </button>
        </nav>
      </div>
      {/* Tab Content */}
      <div className="p-4">
        {activeTab === 'related' && renderRelatedClaims()}
        {activeTab === 'narratives' && renderNarrativeConnections()}
        {activeTab === 'patterns' && renderCrossCountryPatterns()}
      </div>
    </div>
  );
};

export default RelatedClaimsPanel;